import { nonnull } from "@tento-core/assert";
import type { RowTypeDescriptor } from "@tento-lona";
import {
  DefaultLegendLayout,
  type TextMeasurer,
} from "@tento-lona/sheets-ui/sheet/default-legend-layout.ts";
import { TimezoneLegendLayout } from "./column-timezone/support/timezone-plugin.legend-layout.ts";
import {
  DateTime,
  DaysSinceEpoch,
  MsSinceEpoch,
  NaiveDate,
  NaiveDateTime,
  PartialDate,
  TimeOfDay,
  TimezoneRegion,
  Tzname,
  Utc,
} from "@tento-chrono";
import { AsyncStateManager } from "@tento-core/async-state-manager";
import { html, LonaWebComponent } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { ClockIcon, LonaIcons } from "@tento-ui/ui/icons";
import {
  type BindCellContext,
  type RenderPlugin,
  RowOptionsElement,
  type SheetLegendClass,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import { type SheetRow } from "@tento-lona";
import {
  Json,
  type ModelRow,
  type Option,
  type Timescale,
} from "@tento-lona/sheets";
import { Typography } from "@tento-ui/ui/typography";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { DomUtils } from "@tento-ui/dom";
import { $$ } from "@tento-ui/fastdom";
import { TimezonePreferences } from "@tento-lona";
import { COLUMN_TIMEZONE_SPEC, AliasRow, SourceRow, ColumnTimezoneRow } from "@tento-lona/sheets/platform";
import type { PerTypeSpec } from "@tento-lona/sheets/platform";
import { Transition } from "@tento-ui/components/transition";
import { LiveData } from "@tento-core/livedata";
import { Rational } from "@tento-core/rational";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { DateFormat } from "@tento-lona";
import type { Size } from "@tento-ui/layout-utils";
import { liveVariantOf } from "../../sheet/live-variant.ts";
import { deepFrozen } from "@tento-lona/sheets/internal-ui";

/**
 * The renderer plugin for a timezone row: the time in that zone beside each
 * column.
 */
export class ColumnTimezoneRendererPlugin
  implements RenderPlugin.Renderer<
    ColumnTimezoneRendererPlugin.Cell,
    ColumnTimezoneRendererPlugin.Legend,
    ColumnTimezoneRow
  >
{
  static readonly spec: PerTypeSpec = COLUMN_TIMEZONE_SPEC;

  readonly role = "renderer";
  readonly id = "column.timezone";
  readonly rowClass: typeof ColumnTimezoneRow = ColumnTimezoneRow;
  readonly icon: HTMLTemplateElement = LonaIcons.$template(ClockIcon);

  #stateManager = new AsyncStateManager();
  hoverState = new LiveData<Option<{
    fraction: number;
    utc: DateTime<Utc>;
    columnStart: DaysSinceEpoch;
    columnEnd: DaysSinceEpoch;
  }>>(null);

  #dateFormat: LiveData.Readable<DateFormat>;
  constructor(
    readonly makeRowOptions: RowOptionsElement.Factory,
    readonly timezoneStore: TimezonePreferences,
    dateFormat: LiveData.Readable<DateFormat> = new LiveData<DateFormat>(
      "month-day",
    ),
  ) {
    this.#dateFormat = dateFormat;
  }

  get descriptorOverride(): RowTypeDescriptor.Override {
    return {
      buildLabel: (row: SheetRow): string => {
        const attributes =
          row.reconciledAttributes as Option<ColumnTimezoneRendererPlugin.Attributes>;
        const tzname = attributes?.tz;
        if (tzname) return `Timezone: ${tzname}`;
        const defaultTz = this.timezoneStore.effectiveTimezoneName();
        return `Timezone (Default): ${defaultTz}`;
      },
    };
  }

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string> {
    return "";
  }

  makeCell(): ColumnTimezoneRendererPlugin.Cell {
    return ColumnTimezoneRendererPlugin.Cell.make();
  }

  async bindCell(
    $cell: ColumnTimezoneRendererPlugin.Cell,
    { sheet, row, sheetRow, column, handle }: BindCellContext<ColumnTimezoneRow>,
  ) {
    const defaultTz = await this.timezoneStore.loadEffectiveRegion();

    // Resolve parent row's timezone so cell ticks use the same reference
    // frame as CalendarLegend (parent's tz, not the column/mapper tz).
    // Calendar rows use "timezone" attribute, not "tz".
    const parentRow = sheet.joinedTree.parentOf(sheetRow);
    const parentTzname = Json.string(parentRow?.attributes() ?? null, "timezone");
    const parentZone = parentTzname ? Tzname.parseOpt(parentTzname) : null;
    const parentTz = parentZone != null ? await TimezoneRegion.get(parentZone) : null;

    const tzname = row.tz;
    const tzpromise = tzname ? TimezoneRegion.get(tzname) : Promise.resolve(null);
    const stillValid = this.#stateManager.bind($cell, tzpromise);
    tzpromise.then((tz) => {
      if (!stillValid(tzpromise)) return;
      const pd = column.discrete();
      if (parentTz) {
        pd[1] = parentTz;
      }
      $cell.pluginRef = this;
      $cell.bind(pd, tz ?? defaultTz, handle, sheetRow, column);
    });
  }

  onBindRow(_handle: SheetDelegate, _row: SheetRow.Joined.Handle): void {}

  loadData = null;

  onResize(
    _$cell: ColumnTimezoneRendererPlugin.Cell,
    _context: BindCellContext<ColumnTimezoneRow>,
  ): void {}
  onViewportColumnsChanged(): void {}
  onRenderedColumnsWillChange(): void {}

  legendFactory: SheetLegendClass<ColumnTimezoneRendererPlugin.Legend> = (() => {
    const plugin = this;
    return class {
      static make(): ColumnTimezoneRendererPlugin.Legend {
        return ColumnTimezoneRendererPlugin.Legend.make();
      }

      static async bind(
        $legend: ColumnTimezoneRendererPlugin.Legend,
        row: SheetRow.Joined.Handle,
        handle: SheetDelegate,
        _sheetTimescale: Timescale,
      ): Promise<void> {
        $legend.dateFormat = plugin.#dateFormat.get();
        $legend.pluginRef = plugin;
        await $legend.bind(row, handle);
      }

      static computeWidth(
        row: SheetRow.Joined.Handle,
        measure: TextMeasurer,
      ): number {
        const zone = timezoneLegendZone(row);
        const tz = zone.kind === "zone"
          ? zone.tzname
          : zone.kind === "label"
          ? zone.text
          : plugin.timezoneStore.effectiveTimezoneName();
        return TimezoneLegendLayout.width({ tz }, measure);
      }
    };
  })();
}

function findRefTzGapTransition(
  ndt: NaiveDateTime,
  transitions: TimezoneRegion.Transition[],
): Option<MsSinceEpoch> {
  for (const transition of transitions) {
    const transitionDate = transition.after.time.ndt.date;
    if (!ndt.date.equals(transitionDate)) continue;

    const beforeOffsetHrs = transition.before.time.tz.info.offset.toHrsF;
    const afterOffsetHrs = transition.after.time.tz.info.offset.toHrsF;
    if (afterOffsetHrs <= beforeOffsetHrs) continue; // not spring-forward

    const gapStartMs = transition.before.time.ndt.time.toMs;
    const gapSizeMs = (afterOffsetHrs - beforeOffsetHrs) * 3600000;
    const gapEndMs = gapStartMs + gapSizeMs;
    const currentTimeMs = ndt.time.toMs;

    if (currentTimeMs >= gapStartMs && currentTimeMs < gapEndMs) {
      return transition.before.mse;
    }
  }
  return null;
}

/**
 * Types and values that go with `ColumnTimezoneRendererPlugin`: `Attributes`,
 * `rowTzRegion`, `CellTheme`, `buildCellTheme`, `DEFAULT_COLOR_MAP`, `Cell`,
 * and 2 more.
 */
export namespace ColumnTimezoneRendererPlugin {
  export type Attributes = {
    tz?: Tzname;
  };

  /**
   * Resolve the `TimezoneRegion` configured for a timezone row.
   *
   * Accepts either a joined-row Client (legacy callers — standalone
   * timezone-row legend) or a rows-side `SheetRow` (new alias-driven
   * walker that traverses `row.variant.children`). For a joined
   * Client, the rows-side variant is normalized via `row.variant`.
   */
  export async function rowTzRegion(
    row: SheetRow.Joined.Handle | ModelRow,
  ): Promise<Option<TimezoneRegion>> {
    const tzname = rowTzName(row);
    return tzname == null ? null : await TimezoneRegion.get(tzname);
  }

  /** One hour's cell colors: the background, and the text color when it differs. */
  export type CellTheme = {
    readonly bg: string;
    /** The text color, `null` when it is the default. */
    readonly fgText: Option<string>;
  };
  export function buildCellTheme(fn: (hr24: number) => CellTheme): CellTheme[] {
    const r = new Array(24);
    for (let i = 0; i < 24; ++i) r[i] = fn(i);
    return r;
  }
  export const DEFAULT_COLOR_MAP: readonly CellTheme[] = deepFrozen(buildCellTheme((hr24) => {
    if (hr24 <= 5) {
      return {
        bg: "#95b3d7",
        fgText: "var(--text-pri-inv)",
      };
    } else if (hr24 <= 7) {
      return {
        bg: "#edfbfe",
        fgText: null,
      };
    } else if (hr24 <= 17) {
      return {
        bg: "#fffff3",
        fgText: null,
      };
    } else if (hr24 <= 21) {
      return {
        bg: "#edfbfe",
        fgText: null,
      };
    } else {
      return {
        bg: "#95b3d7",
        fgText: "var(--text-pri-inv)",
      };
    }
  }));

  @component({
    name: "lona-timezone-plugin-cell",
  })
  export class Cell extends LonaWebComponent {
    #$transition: Transition = this.hydrate("transition");
    #$root: HTMLElement = this.$("root");
    #$hoverLine: HTMLElement = this.$("hover-line");
    #$currentTimeLine: HTMLElement = this.$("current-time-line");

    pd: Option<PartialDate.WithTz> = null;
    tz: Option<TimezoneRegion> = null;
    handle: Option<SheetDelegate> = null;
    row: Option<SheetRow.Joined.Handle> = null;
    pluginRef: Option<ColumnTimezoneRendererPlugin> = null;
    #column: Option<ColumnIndex.Key.Resolved.Default> = null;
    #transitionFixedChildIds: string[] = [];

    _size: Option<Size> = null;
    onSizeChange: Option<(s: Size) => void> = (s) => {
      this._size = s;
      this.queueRender();
      if (this.handle) {
        this.#updateCurrentTimeLine(this.handle.state.currentTime.get());
      }
    };

    #isHovered = false;
    #refTzTransitions: TimezoneRegion.Transition[] = [];

    #handlePointerEnter = () => {
      this.#isHovered = true;
    };

    #handlePointerLeave = () => {
      this.#isHovered = false;
      this.pluginRef?.hoverState.update(null);
    };

    #handlePointerPosition(
      pos: Option<{ viewportX: number; viewportY: number; contentX: number }>,
      handle: SheetDelegate,
    ): void {
      if (!this.#isHovered) return;
      if (!pos || !this.pd || !this.#column || !this.pluginRef) {
        this.pluginRef?.hoverState.update(null);
        return;
      }

      const colOffset = handle.state.getColumnOffsetPx(this.#column.offset);
      const cellWidth =
        this._size?.width ?? handle.state.getColumnWidthPx(this.#column.offset);
      const fraction = Math.max(
        0,
        Math.min(1, (pos.contentX - colOffset) / cellWidth),
      );

      const start = this.pd[0].start;
      const end = this.pd[0].end;
      const refTz = this.pd[1];
      const numDays = end.dse - start.dse;
      const numHrs = numDays * 24;
      const totalHours = fraction * numHrs;
      const dayOffset = Math.floor(totalHours / 24);
      const hourFrac = totalHours - dayOffset * 24;
      const hrs = Math.floor(hourFrac);
      const mins = Math.floor((hourFrac - hrs) * 60);

      const date = NaiveDate.fromDse((start.dse + dayOffset) as DaysSinceEpoch);
      const ndt = date.withTime(TimeOfDay.fromHms({ hrs, mins }));

      const gapTransitionMse = findRefTzGapTransition(
        ndt,
        this.#refTzTransitions,
      );
      const timepoint =
        gapTransitionMse != null
          ? DateTime.fromMse(gapTransitionMse, Utc)
          : refTz.toWallClock(ndt).toUtc();

      this.pluginRef.hoverState.update({
        fraction,
        utc: timepoint,
        columnStart: start.dse,
        columnEnd: end.dse,
      });
    }

    bind(
      pd: PartialDate.WithTz,
      tz: TimezoneRegion,
      handle: SheetDelegate,
      row: SheetRow.Joined.Handle,
      column: ColumnIndex.Key.Resolved.Default,
    ) {
      this.pd = pd;
      this.tz = tz;
      this.handle = handle;
      this.row = row;
      this.#column = column;

      const start = pd[0].start;
      const end = pd[0].end;
      const refTz = pd[1];
      const startMse = refTz
        .toWallClock(start.withTime(TimeOfDay.fromHms({ hrs: 0 })))
        .toUtc().mse;
      const endMse = refTz
        .toWallClock(end.withTime(TimeOfDay.fromHms({ hrs: 0 })))
        .toUtc().mse;

      this.#refTzTransitions = refTz.transitionsBetween({
        start: startMse,
        end: endMse,
      });

      this.addEventListener("pointerenter", this.#handlePointerEnter);
      this.addEventListener("pointerleave", this.#handlePointerLeave);

      handle.state.pointerPosition.addListener(this, (pos) => {
        this.#handlePointerPosition(pos, handle);
      });

      nonnull(this.pluginRef, "ColumnTimezoneRow.bind: makeCell sets pluginRef").hoverState.addListener(this, (state) => {
        if (!state || !this.pd) {
          this.#$hoverLine.toggleAttribute("data-visible", false);
          return;
        }

        const cellWidth = this._size?.width ?? 0;
        const start = this.pd[0].start.dse;
        const end = this.pd[0].end.dse;
        let xOffset: number;

        if (state.columnStart === start) {
          // This is the hovered column
          xOffset = state.fraction * cellWidth;
        } else if (state.columnEnd === start) {
          // Hovered column is immediately to our left
          xOffset = (state.fraction - 1) * cellWidth;
        } else if (state.columnStart === end) {
          // Hovered column is immediately to our right
          xOffset = (state.fraction + 1) * cellWidth;
        } else {
          this.#$hoverLine.toggleAttribute("data-visible", false);
          return;
        }

        this.#$hoverLine.style.transform = `translateX(${xOffset}px)`;
        this.#$hoverLine.toggleAttribute("data-visible", true);
      });

      handle.state.currentTime.addListener(
        this,
        (currentTime) => this.#updateCurrentTimeLine(currentTime),
        LiveData.TriggerImmediately,
      );

      this.queueRender();
    }

    #updateCurrentTimeLine(currentTime: DateTime<Utc>) {
      if (!this.pd) return;
      const start = this.pd[0].start;
      const end = this.pd[0].end;
      const refTz = this.pd[1];
      const numDays = end.dse - start.dse;
      const numHrs = numDays * 24;
      const refWall = refTz.toTz(currentTime);
      const refDateDse = refWall.date.dse;
      const cellWidth = this._size?.width ?? 0;
      if (cellWidth === 0) return;

      if (refDateDse >= start.dse && refDateDse < end.dse) {
        const dayOffset = refDateDse - start.dse;
        const hourFrac = refWall.time.hrs + refWall.time.mins / 60;
        const totalHours = dayOffset * 24 + hourFrac;
        const fraction = totalHours / numHrs;
        const xOffset = fraction * cellWidth;
        this.#$currentTimeLine.style.transform = `translateX(${xOffset}px)`;
        this.#$currentTimeLine.toggleAttribute("data-visible", true);
      } else {
        this.#$currentTimeLine.toggleAttribute("data-visible", false);
      }
    }

    #dismissTransitionFixedChildren() {
      if (!this.handle) return;
      for (const id of this.#transitionFixedChildIds) {
        this.handle.fixedChild.dismiss(id, "instant");
      }
      this.#transitionFixedChildIds = [];
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.#dismissTransitionFixedChildren();
      this.removeEventListener("pointerenter", this.#handlePointerEnter);
      this.removeEventListener("pointerleave", this.#handlePointerLeave);
      this.handle?.state.pointerPosition.removeListener(this);
      this.pluginRef?.hoverState.removeListener(this);
      this.handle?.state.currentTime.removeListener(this);
    }

    #renderQueued = false;
    queueRender() {
      if (this.#renderQueued) return;
      this.#renderQueued = true;
      $$.mutate(() => {
        this.#renderQueued = false;
        this.renderNow();
      });
    }

    renderNow(theme: readonly CellTheme[] = DEFAULT_COLOR_MAP) {
      if (!this.isConnected) return;
      DomUtils.clearChildren(this.#$root);

      const tz = this.tz;
      const pd = this.pd;
      const widthPx = this._size?.width;

      if (!tz || !pd || !widthPx) return;

      const start = pd[0].start;
      const end = pd[0].end;
      const numDays = end.dse - start.dse;
      const numHrs = numDays * 24;
      const cellWidth = widthPx / numHrs;

      if (cellWidth < 4) {
        this.#dismissTransitionFixedChildren();
        const $transitionSegments = this.$("transition-segments");
        DomUtils.clearChildren($transitionSegments);

        const refTz = pd[1];
        const startMse = refTz
          .toWallClock(start.withTime(TimeOfDay.fromHms({ hrs: 0 })))
          .toUtc().mse;
        const endMse = refTz
          .toWallClock(end.withTime(TimeOfDay.fromHms({ hrs: 23, mins: 59 })))
          .toUtc().mse;

        const transitions = tz.transitionsBetween({
          start: startMse,
          end: endMse,
        });

        for (const [i, transition] of transitions.entries()) {
          // Use just-before-transition UTC to get pre-transition offset from refTz.
          // At exact transition.before.mse, toTz uses post-transition offset,
          // which places the segment at hour 3.0 instead of gap start (hour 2.0).
          const justBeforeUtc = DateTime.fromMse(
            MsSinceEpoch.of(transition.before.mse - 1),
            Utc,
          );
          const refWall = refTz.toTz(justBeforeUtc);
          const refDateDse = refWall.date.dse;

          if (refDateDse < start.dse || refDateDse >= end.dse) continue;

          const dayOffset = refDateDse - start.dse;
          const hourFrac = refWall.time.hrs + refWall.time.mins / 60;
          const totalHours = dayOffset * 24 + hourFrac;
          const beforeOffset = transition.before.time.tz.info.offset.toHrsF;
          const afterOffset = transition.after.time.tz.info.offset.toHrsF;
          const durationHrs = Math.abs(afterOffset - beforeOffset);
          const segmentWidth = Math.max((durationHrs / numHrs) * widthPx, 2);

          // Center segment on the transition instant
          const xOffset = (totalHours / numHrs) * widthPx - segmentWidth / 2;

          const $segment = document.createElement("div");
          $segment.className = "transition-segment";
          $segment.assignStyles({
            left: `${xOffset}px`,
            width: `${segmentWidth}px`,
          });
          $transitionSegments.appendChild($segment);

          // Label as fixed child so it isn't clipped by cell overflow
          if (this.handle && this.row && this.#column) {
            const beforeAbbr = transition.before.time.tz.info.tzabbr;
            const afterAbbr = transition.after.time.tz.info.tzabbr;
            let labelText: string;
            if (
              beforeAbbr &&
              afterAbbr &&
              beforeAbbr !== "%z" &&
              afterAbbr !== "%z"
            ) {
              labelText = `${beforeAbbr} \u2192 ${afterAbbr}`;
            } else {
              labelText = `${transition.before.time.tz.info.offset.toString()} \u2192 ${transition.after.time.tz.info.offset.toString()}`;
            }

            const $label = document.createElement("span");
            $label.textContent = labelText;
            $label.assignStyles({
              fontSize: "9px",
              color: "var(--text-ter)",
              whiteSpace: "nowrap",
              pointerEvents: "none",
              transform: "translateX(-50%)",
            });

            const totalMinutes = Math.round(totalHours * 60);
            const totalMinutesRange = numHrs * 60;
            const subindex = new Rational(totalMinutes, totalMinutesRange);

            const id = `tz-tr-${this.row.id}-${start.dse}-${i}`;
            this.#transitionFixedChildIds.push(id);

            this.handle.fixedChild.reveal(
              id,
              $label,
              {
                x: this.#column.withSubindex(subindex),
                y: this.row.id,
              },
              {
                widthBehavior: "auto",
                animate: false,
                inset: {
                  top: 8,
                  left: 0,
                  right: 0,
                  bottom: 0,
                },
              },
            );
          }
        }

        this.#$transition.bindState("after");
        return;
      }

      this.#dismissTransitionFixedChildren();

      // Reference timezone from column - used to iterate hours in reference time
      const refTz = pd[1];

      // Initialize prevLocalHr with the hour before the first cell
      const prevDayLastHour = refTz
        .toWallClock(
          NaiveDate.fromDse((start.dse - 1) as DaysSinceEpoch).withTime(
            TimeOfDay.fromHms({ hrs: 23 }),
          ),
        )
        .toUtc();
      const prevDayLastHourLocal = tz.toTz(prevDayLastHour);
      let prevLocalHr = prevDayLastHourLocal.time.hrs;

      for (let dse = start.dse; dse < end.dse; ++dse) {
        for (let hrs = 0; hrs < 24; ++hrs) {
          // Convert reference timezone hour to UTC for proper display
          const dtref = refTz
            .toWallClock(
              NaiveDate.fromDse(dse).withTime(TimeOfDay.fromHms({ hrs })),
            )
            .toUtc();
          const dt = tz.toTz(dtref);
          const localHrs = dt.time.hrs;
          const isDuplicate = localHrs === prevLocalHr;

          const $c = HourCell.make();
          $c.bind(localHrs);
          $c.bindTheme(
            nonnull(theme[dt.time.hrs], "a cell theme per hour"),
            nonnull(theme[0], "a cell theme per hour").bg,
            isDuplicate ? "repeated" : "first",
          );
          this.#$root.appendChild($c);
          prevLocalHr = localHrs;
        }
      }

      this.assignStyles({
        "--num-hrs": String(numHrs),
      });
      this.#$transition.bindState("before");
    }

    static $styles: Style.Compat[] = [
      css`
        :host {
          position: relative;
          height: 100%;
          --transition: none;
        }

        #transition {
          height: 100%;
          width: 100%;
        }

        #root {
          height: 100%;
          width: calc(100% - 2px);
          margin-left: 1px;
          display: grid;
          grid-template-columns: repeat(var(--num-hrs), 1fr);
          gap: 2px;
          pointer-events: none;
        }

        @container (max-width: 24px) {
          #root {
            width: calc(100% - 1px);
          }
        }

        @container (max-width: 12px) {
          #root {
            width: calc(100%);
          }
        }

        #graph {
          width: 100%;
          height: 100%;
          justify-content: center;
          position: relative;
        }

        #graph-line {
          height: 2px;
          width: 100%;
          background: var(--divider);
        }

        #transition-segments {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
        }

        .transition-segment {
          position: absolute;
          height: 4px;
          background: var(--red);
          top: 50%;
          transform: translateY(-50%);
          pointer-events: none;
          border-radius: 1px;
          min-width: 2px;
        }

        #hover-line {
          position: absolute;
          top: -1px;
          bottom: -3px;
          left: -1px;
          width: 2px;
          background: var(--red);
          pointer-events: none;
          display: none;
          will-change: transform;
          border-radius: 1px;
        }

        #hover-line[data-visible] {
          display: block;
        }

        #current-time-line {
          position: absolute;
          top: -1px;
          bottom: -3px;
          left: -1px;
          width: 2px;
          background: var(--divider-pri);
          pointer-events: none;
          display: none;
          will-change: transform;
          border-radius: 1px;
        }

        #current-time-line[data-visible] {
          display: block;
        }
      `,
    ];
    static $html: Option<HTMLTemplateElement> = html`
      <std-transition id=transition>
        <div id=root slot=before></div>
        <std-col id=graph slot=after>
          <div id=graph-line></div>
        </std-col>
      </std-transition>
      <div id=hover-line></div>
      <div id=current-time-line></div>
      <div id=transition-segments></div>
    `;
  }

  @component({
    name: "lona-timezone-plugin-hour-cell",
  })
  export class HourCell extends LonaWebComponent {
    $title: HTMLElement = this.$("title");
    $subitle: HTMLElement = this.$("subtitle");

    bind(hr24: number) {
      const hr12 = hr24 % 12;
      this.$title.textContent = String(hr12 === 0 ? 12 : hr12);
      this.$subitle.textContent = hr24 < 12 ? "am" : "pm";
      if (hr24 === 0) {
        this.toggleAttribute("start", true);
      } else if (hr24 === 23) {
        this.toggleAttribute("end", true);
      }
    }

    /**
     * Paint the hour: its theme, the column's border colour, and whether the
     * local hour is the `"first"` of its label or a `"repeated"` one (the hour
     * a DST fall-back shows twice), which is marked red.
     */
    bindTheme(t: CellTheme, borderColor: string, hour: "first" | "repeated") {
      this.style.setProperty("--border-color", borderColor);
      this.style.setProperty("--cell-color", hour === "repeated" ? "var(--red)" : t.bg);
      this.$title.style.setProperty("color", t.fgText ?? "");
      this.$subitle.style.setProperty("color", t.fgText ?? "");
    }

    static $styles: Style.Compat[] = [
      css`
        :host {
          container-type: inline-size;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;

          position: relative;
          height: 100%;
          color: var(--text-sec);
        }

        :host::before {
          position: absolute;
          content: "";
          background: var(--cell-color);
          z-index: -1;
          top: 2px;
          bottom: 2px;

          left: -1px;
          right: -1px;
          border-top: 1px solid var(--border-color);
          border-bottom: 1px solid var(--border-color);
        }

        :host([start])::before {
          left: 2px;
          border-top-left-radius: 4px;
          border-bottom-left-radius: 4px;
        }

        :host([end])::before {
          right: 2px;
          border-top-right-radius: 4px;
          border-bottom-right-radius: 4px;
        }

        #title {
          --margin-bottom: 2px;
        }

        #subtitle {
          --font-size: var(--text-7px);
          color: var(--text-ter);
        }

        @container (max-width: 24px) {
          #title {
            --font-size: var(--text-8px);
          }
          #subtitle {
            --font-size: var(--text-6px);
          }
          :host([start])::before {
            left: 1px
          }
          :host([end])::before {
            right: 1px
          }
        }

        @container (max-width: 12px) {
          #title,
          #subtitle {
            display: none;
          }
          :host([start])::before {
            left: 0.5px
          }
          :host([end])::before {
            right: 0.5px
          }
        }
      `,
    ];
    static $html: Option<HTMLTemplateElement> = html`
      <p id=title></p>
      <p id=subtitle></p>
    `;
  }

  @component({
    name: "lona-timezone-plugin-legend",
  })
  export class Legend extends LonaWebComponent {
    #$tzname: HTMLElement = this.$("tzname");
    #$offset: HTMLElement = this.$("offset");
    #$localTime: HTMLElement = this.$("local-time");
    #$localDate: HTMLElement = this.$("local-date");
    #$timeIndicator: HTMLElement = this.$("time-indicator");

    dateFormat: DateFormat = "month-day";
    pluginRef: Option<ColumnTimezoneRendererPlugin> = null;

    #tz: Option<TimezoneRegion> = null;
    #row: Option<SheetRow.Joined.Handle> = null;
    #handle: Option<SheetDelegate> = null;

    async bind(row: SheetRow.Joined.Handle, handle: SheetDelegate) {
      // Cleanup previous GESTURE_MANAGER handler before rebinding
      GESTURE_MANAGER.unregister(this.$("root"));

      this.#row = row;
      this.#handle = handle;

      GESTURE_MANAGER.addPointerEvent(this.$("root"), {
        onClick: () => {
          handle.rebind.revealRowOptionsPopover(row, "default");
        },
      });
      const plugin = nonnull(this.pluginRef, "ColumnTimezoneRendererPlugin.Legend.bind: the factory sets pluginRef");
      const zone = timezoneLegendZone(row);
      if (zone.kind === "label") {
        this.#tz = null;
        this.bindLabel(zone.text);
      } else {
        const tz = zone.kind === "zone"
          ? await TimezoneRegion.get(zone.tzname)
          : await plugin.timezoneStore.loadEffectiveRegion();
        this.#tz = tz;
        this.bindTz(tz, handle);
      }
      plugin.hoverState.addListener(this, (state) => {
        if (state && this.#tz && this.#handle) {
          const local = this.#tz.toTz(state.utc);
          this.#$localTime.textContent = Legend.formatTime(local);
          this.#$localDate.textContent = Legend.formatDate(
            local,
            this.dateFormat,
          );
          this.#$timeIndicator.style.background = Legend.getTimeIndicatorColor(
            local.time.hrs,
          );
          this.#$offset.textContent = Legend.formatOffset(local);
        } else if (this.#tz && this.#handle) {
          const local = this.#tz.toTz(this.#handle.state.currentTime.get());
          this.#$localTime.textContent = Legend.formatTime(local);
          this.#$localDate.textContent = Legend.formatDate(
            local,
            this.dateFormat,
          );
          this.#$timeIndicator.style.background = Legend.getTimeIndicatorColor(
            local.time.hrs,
          );
          this.#$offset.textContent = Legend.formatOffset(local);
        }
      });
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.pluginRef?.hoverState.removeListener(this);
    }

    /** Show `text` in place of a zone: the row configures none, so no time, date or offset. */
    bindLabel(text: string): this {
      this.setAttribute("dbg-tz", "");
      this.#$tzname.textContent = text;
      this.#$offset.textContent = "";
      this.#$localTime.textContent = "";
      this.#$localDate.textContent = "";
      this.#$timeIndicator.style.background = "";
      return this;
    }

    bindTz(tz: TimezoneRegion, handle: SheetDelegate): this {
      this.setAttribute("dbg-tz", tz?.fullname ?? "");
      const local = tz.toTz(handle.state.currentTime.get());
      this.#$tzname.textContent = local.tz.info.tzname ?? "";
      this.#$offset.textContent = Legend.formatOffset(local);
      this.#$localTime.textContent = Legend.formatTime(local);
      this.#$localDate.textContent = Legend.formatDate(local, this.dateFormat);
      this.#$timeIndicator.style.background = Legend.getTimeIndicatorColor(
        local.time.hrs,
      );
      return this;
    }

    static formatTime(dt: DateTime<string>): string {
      const hrs = dt.time.hrs;
      const mins = dt.time.mins;
      const isPM = hrs >= 12;
      const hrs12 = hrs % 12 || 12;
      const period = isPM ? "p" : "a";
      return `${hrs12}:${String(mins).padStart(2, "0")}${period}`;
    }

    static formatDate(
      dt: DateTime<string>,
      dateFormat: DateFormat = "month-day",
    ): string {
      return NaiveDate.Formatter.render(dt.date, {
        dateFormat,
        shortMonth: true,
        includeWeekday: true,
      });
    }

    static getTimeIndicatorColor(hrs: number): string {
      if (hrs >= 22 || hrs < 5) {
        return "var(--red)";
      } else if (hrs >= 8 && hrs < 17) {
        return "var(--green)";
      } else {
        return "var(--yellow)";
      }
    }

    static formatOffset(dt: DateTime<string>): string {
      const tzabbr = dt.tz.info.tzabbr;
      const offsetStr = dt.tz.info.offset.toString();
      return (tzabbr && tzabbr !== "%z" ? tzabbr + "  " : "") + offsetStr;
    }

    static $styles: Style.Compat[] = [
      Typography.$maxLines,
      css`
        :host {
          flex-grow: 1;
          height: 100%;
          color: var(--text-sec);
          display: flex;
        }

        #root {
          position: relative;
          flex-grow: 1;
          align-self: stretch;
          align-items: center;
          margin-right: 4px;
          cursor: pointer;
        }

        #root:hover::before {
          position: absolute;
          content: "";
          inset: 0px;
          border-radius: 8px;
          background: var(--bg-hover);
        }

        #content {
          padding-inline-start: 4px;
          padding-inline-end: 8px;
        }

        #spacer {
          width: 12px;
          flex-shrink: 0;
        }

        .header {
          --font-size: var(--small-size);
          --line-height-ratio: 1.1;
        }

        .caption {
          color: var(--text-ter);
          --font-size: var(--text-8px);
        }

        #local-time {
          text-align: end;
        }

        #time-indicator {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          margin-left: 6px;
          flex-shrink: 0;
        }

        #local-date {
          min-width: 64px;
          text-align: end;
        }
      `,
    ];
    static $html: HTMLTemplateElement = html`
      <std-row id=root>
        <std-row id=content style=flex-grow:1;>
          <std-col id=label style=flex-shrink:1;>
            <std-row style=margin-bottom:8px;>
              <p id=tzname class="header"></p>
            </std-row>
            <p id=offset class="caption max-lines-1"></p>
          </std-col>
          <std-spacer id=spacer></std-spacer>
          <std-col style=flex-shrink:0;width:72px>
            <std-row style=align-items:center;justify-content:end;margin-bottom:8px;>
              <p id=local-time class=header></p>
              <div id=time-indicator></div>
            </std-row>
            <p id=local-date class=caption></p>
          </std-col>
        </std-row>
      </std-row>
    `;
  }
}

/**
 * The zone configured for a timezone row: its renderer's `tz`; for a
 * timezone alias, the `tz` of its data child's renderer, else the alias's
 * own `tz` prop (the input its template builds that child from), so an
 * alias whose child is gone still names the zone it was configured with.
 * `null` when the row configures none.
 */
function rowTzName(row: SheetRow.Joined.Handle | ModelRow): Option<Tzname> {
  const variant: Option<ModelRow> = "variant" in row ? liveVariantOf(row) : row;
  if (variant instanceof SourceRow) return variant.defaultRenderer(ColumnTimezoneRow)?.tz ?? null;
  if (variant instanceof AliasRow) {
    for (const child of variant.children) {
      if (!(child instanceof SourceRow)) continue;
      const tzname = child.defaultRenderer(ColumnTimezoneRow)?.tz;
      if (tzname) return tzname;
    }
    const prop = Json.string(variant.instance().props, "tz");
    return prop == null ? null : Tzname.parseOpt(prop) ?? null;
  }
  return null;
}

/**
 * What a timezone legend shows for `row`: the zone it configures; for an
 * alias that configures none, its own label (else its kind's name), since
 * it has no zone to show and the viewer's would be invented; for any other
 * timezone row without a zone, the viewer's, which its cells use too.
 *
 * Module-level, outside `ColumnTimezoneRendererPlugin`: the published entry
 * re-exports only the plugin, so no published signature names
 * `@tento-chrono`'s `Tzname`, which no published entry exports.
 */
export type TimezoneLegendZone =
  | { readonly kind: "zone"; readonly tzname: Tzname }
  | { readonly kind: "label"; readonly text: string }
  | { readonly kind: "viewer" };

/** `TimezoneLegendZone` for `row`. */
export function timezoneLegendZone(row: SheetRow.Joined.Handle): TimezoneLegendZone {
  const tzname = rowTzName(row);
  if (tzname != null) return { kind: "zone", tzname };
  const variant = liveVariantOf(row);
  if (variant instanceof AliasRow) return { kind: "label", text: row.label ?? variant.displayName };
  return { kind: "viewer" };
}
